import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Divider,
  Stack,
} from "@mui/material";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import { Mousewheel, Keyboard } from "swiper/modules";
import { useNavigate, useLocation } from "react-router-dom";
import ReelCard from "./ReelCard";
import api from "../api/axios"; // ✅ axios instance

const Reels = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const swiperRef = useRef(null);

  const [reels, setReels] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [audioEnabled, setAudioEnabled] = useState(false);

  // 🔥 Load reels via API
  useEffect(() => {
    const loadReels = async () => {
      try {
        const res = await api.get("/reels");
        setReels(res.data.reels || []);
      } catch (err) {
        console.error("Failed to load reels", err);
      } finally {
        setLoading(false);
      }
    };

    loadReels();
  }, []);

  // Restore reel position on back navigation
  useEffect(() => {
    const reelIndex = location.state?.reelIndex;
    if (
      typeof reelIndex === "number" &&
      swiperRef.current?.slideTo
    ) {
      swiperRef.current.slideTo(reelIndex, 0);
    }
  }, [location.state]);

  const activeReel = reels[activeIndex];

  if (loading) {
    return (
      <Box
        sx={{
          height: "calc(100dvh - 64px)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography>Loading reels...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: "100dvh", display: "flex", bgcolor: "#000" }}>
      {/* LEFT: REELS */}
      <Box 
        className="reels-scroll-wrapper"
        onClick={() => setAudioEnabled(true)} 
        sx={{ flex: 1, display: "flex", justifyContent: "center" }}
      >
        <Swiper
          direction="vertical"
          modules={[Mousewheel, Keyboard]}
          mousewheel={{ 
            forceToAxis: true,
            eventsTarget: ".reels-scroll-wrapper"
          }}
          keyboard={{ enabled: true }}
          onSwiper={(swiper) => (swiperRef.current = swiper)}
          onSlideChange={(swiper) => setActiveIndex(swiper.activeIndex)}
          style={{ width: "100%", maxWidth: 480, height: "calc(100dvh - 64px)" }}
        >
          {reels.map((reel, index) => {
            const reelWithoutImage = { ...reel };
            delete reelWithoutImage.imageUrl;
            return (
              <SwiperSlide key={reel.id}>
                <ReelCard
                  reel={reelWithoutImage}
                  index={index}
                  isActive={index === activeIndex}
                  audioEnabled={audioEnabled}
                  onToggleAudio={() => setAudioEnabled((prev) => !prev)}
                  onShopNow={() =>
                    navigate(`/product/${reel.id}`, {
                      state: { reelIndex: index },
                    })
                  }
                />
              </SwiperSlide>
            );
          })}
        </Swiper>
      </Box>

      {/* RIGHT PANEL (DESKTOP) */}
      <Box
        sx={{
          width: 360,
          display: { xs: "none", md: "flex" },
          flexDirection: "column",
          bgcolor: "#111",
          color: "white",
          p: 2,
          gap: 2,
        }}
      >
        {activeReel && (
          <>
            <Stack spacing={1}>
              <Typography fontWeight="bold">
                Why buy from us?
              </Typography>
              <Typography variant="caption">🚚 Free Delivery</Typography>
              <Typography variant="caption">💳 Cash on Delivery</Typography>
              <Typography variant="caption">⭐ 4.5+ Rated Products</Typography>
            </Stack>

            <Divider sx={{ bgcolor: "#333" }} />

            <Stack spacing={1}>
              <Typography fontWeight="bold">Quick Actions</Typography>
              <Button startIcon={<FavoriteBorderIcon />}>
                Wishlist
              </Button>
              <Button startIcon={<LocalOfferIcon />}>
                View Offers
              </Button>
            </Stack>
          </>
        )}
      </Box>
    </Box>
  );
};

export default Reels;